import { useState, useRef, useEffect } from 'react';

// Custom hook to manage all the state for the Rebalance component
export const useRebalanceState = () => {
  // File and group selection state
  const [rebalancingGroups, setRebalancingGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groupHighLevelInfo, setGroupHighLevelInfo] = useState(null);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHighLevelInfo, setIsLoadingHighLevelInfo] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [isLoadingCashActions, setIsLoadingCashActions] = useState(false);
  const [isLoadingSecurities, setIsLoadingSecurities] = useState(false);  const [isLoadingTradeRestrictions, setIsLoadingTradeRestrictions] = useState(false);
  const [isLoadingCashLevels, setIsLoadingCashLevels] = useState({});

  // Error states
  const [error, setError] = useState('');
  const [reportError, setReportError] = useState('');
  const [cashActionsError, setCashActionsError] = useState('');
  const [securitiesError, setSecuritiesError] = useState('');  const [tradeRestrictionsError, setTradeRestrictionsError] = useState('');
  // UI collapse states - initialize from localStorage
  const [isHighLevelCollapsed, setIsHighLevelCollapsed] = useState(() => {
    const saved = localStorage.getItem('isHighLevelCollapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [isCashActionCollapsed, setIsCashActionCollapsed] = useState(() => {
    const saved = localStorage.getItem('isCashActionCollapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [isCashActionGuideCollapsed, setIsCashActionGuideCollapsed] = useState(() => {
    const saved = localStorage.getItem('isCashActionGuideCollapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isSecuritiesInfoCollapsed, setIsSecuritiesInfoCollapsed] = useState(() => {
    const saved = localStorage.getItem('isSecuritiesInfoCollapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [isSecuritiesInfoGuideCollapsed, setIsSecuritiesInfoGuideCollapsed] = useState(() => {
    const saved = localStorage.getItem('isSecuritiesInfoGuideCollapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isTradeRestrictionsCollapsed, setIsTradeRestrictionsCollapsed] = useState(() => {
    const saved = localStorage.getItem('isTradeRestrictionsCollapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [isTradeRestrictionsGuideCollapsed, setIsTradeRestrictionsGuideCollapsed] = useState(() => {
    const saved = localStorage.getItem('isTradeRestrictionsGuideCollapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isDetailedCollapsed, setIsDetailedCollapsed] = useState(() => {
    const saved = localStorage.getItem('isDetailedCollapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isRebalanceCollapsed, setIsRebalanceCollapsed] = useState(() => {
    const saved = localStorage.getItem('isRebalanceCollapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [isRebalanceGuideCollapsed, setIsRebalanceGuideCollapsed] = useState(() => {
    const saved = localStorage.getItem('isRebalanceGuideCollapsed');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Save all collapse states to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('isHighLevelCollapsed', JSON.stringify(isHighLevelCollapsed));
  }, [isHighLevelCollapsed]);

  useEffect(() => {
    localStorage.setItem('isCashActionCollapsed', JSON.stringify(isCashActionCollapsed));
  }, [isCashActionCollapsed]);

  useEffect(() => {
    localStorage.setItem('isCashActionGuideCollapsed', JSON.stringify(isCashActionGuideCollapsed));
  }, [isCashActionGuideCollapsed]);

  useEffect(() => {
    localStorage.setItem('isSecuritiesInfoCollapsed', JSON.stringify(isSecuritiesInfoCollapsed));
  }, [isSecuritiesInfoCollapsed]);

  useEffect(() => {
    localStorage.setItem('isSecuritiesInfoGuideCollapsed', JSON.stringify(isSecuritiesInfoGuideCollapsed));
  }, [isSecuritiesInfoGuideCollapsed]);

  useEffect(() => {
    localStorage.setItem('isTradeRestrictionsCollapsed', JSON.stringify(isTradeRestrictionsCollapsed));
  }, [isTradeRestrictionsCollapsed]);

  useEffect(() => {
    localStorage.setItem('isTradeRestrictionsGuideCollapsed', JSON.stringify(isTradeRestrictionsGuideCollapsed));
  }, [isTradeRestrictionsGuideCollapsed]);

  useEffect(() => {
    localStorage.setItem('isDetailedCollapsed', JSON.stringify(isDetailedCollapsed));
  }, [isDetailedCollapsed]);

  useEffect(() => {
    localStorage.setItem('isRebalanceCollapsed', JSON.stringify(isRebalanceCollapsed));
  }, [isRebalanceCollapsed]);

  useEffect(() => {
    localStorage.setItem('isRebalanceGuideCollapsed', JSON.stringify(isRebalanceGuideCollapsed));
  }, [isRebalanceGuideCollapsed]);

  // Report data states
  const [selectedReport, setSelectedReport] = useState('accounts');
  const [reportData, setReportData] = useState(null);
  const [cashActionsData, setCashActionsData] = useState(null);
  const [securitiesData, setSecuritiesData] = useState(null);  const [tradeRestrictionsData, setTradeRestrictionsData] = useState(null);
  // Display preferences - initialize from localStorage
  const [hideDollarValues, setHideDollarValues] = useState(() => {
    const saved = localStorage.getItem('hideDollarValues');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Save hideDollarValues to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('hideDollarValues', JSON.stringify(hideDollarValues));
  }, [hideDollarValues]);

  // Trade form state
  const [expandedTradeRows, setExpandedTradeRows] = useState(new Set());
  const [tradeFormData, setTradeFormData] = useState({});
  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [accountMapping, setAccountMapping] = useState({});
  const [securityLevelModelSymbols, setSecurityLevelModelSymbols] = useState({});
  const [loadingSymbolsFor, setLoadingSymbolsFor] = useState({});
  const [symbolHoldings, setSymbolHoldings] = useState({});
  const [loadingAccountHoldings, setLoadingAccountHoldings] = useState({});
  const [loadingCombinedTargets, setLoadingCombinedTargets] = useState({});
  const [loadingCashLevels, setLoadingCashLevelsApi] = useState({});

  // Cash tracking state
  const [accountCashLevels, setAccountCashLevels] = useState({});

  // Synchronous locks to prevent race conditions in React Strict Mode
  const symbolLoadingLock = useRef(new Set());
  const accountHoldingsLoadingLock = useRef(new Set());
  const combinedTargetLoadingLock = useRef(new Set());
  const cashLevelsLoadingLock = useRef(new Set());

  // AbortController references for cancelling in-flight requests
  const combinedTargetAbortControllers = useRef(new Map());
  const cashLevelsAbortControllers = useRef(new Map());

  // Debounce timers to prevent rapid API calls
  const cashLevelsDebounceTimers = useRef(new Map());
  const combinedTargetDebounceTimers = useRef(new Map());

  return {
    // File and group selection
    rebalancingGroups, setRebalancingGroups,
    selectedGroup, setSelectedGroup,
    groupHighLevelInfo, setGroupHighLevelInfo,

    // Loading states
    isLoading, setIsLoading,
    isLoadingHighLevelInfo, setIsLoadingHighLevelInfo,
    isLoadingReport, setIsLoadingReport,
    isLoadingCashActions, setIsLoadingCashActions,
    isLoadingSecurities, setIsLoadingSecurities,    isLoadingTradeRestrictions, setIsLoadingTradeRestrictions,
    isLoadingCashLevels, setIsLoadingCashLevels,

    // Error states
    error, setError,
    reportError, setReportError,
    cashActionsError, setCashActionsError,
    securitiesError, setSecuritiesError,    tradeRestrictionsError, setTradeRestrictionsError,

    // UI collapse states
    isHighLevelCollapsed, setIsHighLevelCollapsed,
    isCashActionCollapsed, setIsCashActionCollapsed,
    isCashActionGuideCollapsed, setIsCashActionGuideCollapsed,
    isSecuritiesInfoCollapsed, setIsSecuritiesInfoCollapsed,
    isSecuritiesInfoGuideCollapsed, setIsSecuritiesInfoGuideCollapsed,
    isTradeRestrictionsCollapsed, setIsTradeRestrictionsCollapsed,
    isTradeRestrictionsGuideCollapsed, setIsTradeRestrictionsGuideCollapsed,
    isDetailedCollapsed, setIsDetailedCollapsed,
    isRebalanceCollapsed, setIsRebalanceCollapsed,
    isRebalanceGuideCollapsed, setIsRebalanceGuideCollapsed,

    // Report data
    selectedReport, setSelectedReport,
    reportData, setReportData,
    cashActionsData, setCashActionsData,
    securitiesData, setSecuritiesData,    tradeRestrictionsData, setTradeRestrictionsData,

    // Display preferences
    hideDollarValues, setHideDollarValues,

    // Trade form state
    expandedTradeRows, setExpandedTradeRows,
    tradeFormData, setTradeFormData,
    availableSymbols, setAvailableSymbols,
    availableAccounts, setAvailableAccounts,
    accountMapping, setAccountMapping,
    securityLevelModelSymbols, setSecurityLevelModelSymbols,
    loadingSymbolsFor, setLoadingSymbolsFor,
    symbolHoldings, setSymbolHoldings,
    loadingAccountHoldings, setLoadingAccountHoldings,
    loadingCombinedTargets, setLoadingCombinedTargets,
    loadingCashLevels, setLoadingCashLevelsApi,

    // Cash tracking
    accountCashLevels, setAccountCashLevels,

    // Refs
    symbolLoadingLock,
    accountHoldingsLoadingLock,
    combinedTargetLoadingLock,
    cashLevelsLoadingLock,
    combinedTargetAbortControllers,
    cashLevelsAbortControllers,
    cashLevelsDebounceTimers,
    combinedTargetDebounceTimers
  };
};
